import {
  Button,
  Col,
  Form,
  InputNumber,
  Layout,
  notification,
  Row,
  Select,
  Space,
  Typography,
  Input,
} from "antd";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ISettingsService } from "@shared/interfaces/settings-service.interface";
import { SettingsDto } from "@shared/types/settings/settings.dto";
import {
  type SettingsFormValues,
  settingsFormValuesToDto,
} from "@shared/types/settings/settings-form.types";
import { useEffect } from "react";
import { useMutation, useQuery } from "react-query";
import { SelectOptions } from "./Settings.const";
import styles from "./Settings.module.scss";
import { useWatch } from "antd/es/form/Form";

const Settings = () => {
  const [form] = Form.useForm<SettingsFormValues>();

  const freeDraftTokensEnabledString = useWatch("freeDraftTokensEnabled", form);
  const freeDraftTokensEnabled = Boolean(Number(freeDraftTokensEnabledString));

  const settingsService = appInject<ISettingsService>(
    DI_TOKENS.settingsService,
  );

  const getSettingsQuery = useQuery<SettingsDto>(
    ["get-settings"],
    () => settingsService.get(),
    {
      refetchOnMount: true,
    },
  );

  const updateSettingsMutation = useMutation(
    (data: SettingsDto): Promise<SettingsDto> => settingsService.update(data),
    {
      onSuccess: () => {
        notification.success({
          message: "Settings updated successfully",
        });
      },
    },
  );

  useEffect(() => {
    if (getSettingsQuery.data) {
      const d = getSettingsQuery.data;
      const formValues: SettingsFormValues = {
        usableDraftTokensPerDay: d.usableDraftTokensPerDay,
        draftsPerReferral: d.draftsPerReferral,
        draftTokensPerDay: d.draftTokensPerDay,
        viewSolutionLimitPerDay: d.viewSolutionLimitPerDay,
        initialFreeSolutionLimit: d.initialFreeSolutionLimit,
        sessionReplayCount: d.sessionReplayCount,
        minScoreToUnlockRankedMode: d.minScoreToUnlockRankedMode,
        draftsToUnlockRankedMode: d.draftsToUnlockRankedMode,
        maxFinishPositionToUnlockRankedMode:
          d.maxFinishPositionToUnlockRankedMode ?? 50,
        rankedRoyaleDraftsPerDay: d.rankedRoyaleDraftsPerDay ?? 5,
        forceUpdateApp: d.forceUpdateApp ? "1" : "0",
        freeDraftTokensEnabled: d.freeDraftTokensEnabled ? "1" : "0",
        inAppPurchaseEnabled: d.inAppPurchaseEnabled ? "1" : "0",
        maintenanceModeEnabled: d.maintenanceModeEnabled ? "1" : "0",
        offseasonAutoStartEnabled: d.offseasonAutoStartEnabled ? "1" : "0",
        extras: JSON.stringify(d.extras || {}, null, 2),
      };
      form.setFieldsValue(formValues);
    }
  }, [form, getSettingsQuery.data]);

  const handleFinish = (values: SettingsFormValues) => {
    let payload: SettingsDto;
    try {
      payload = settingsFormValuesToDto(values);
    } catch (e) {
      if (e instanceof Error && e.message === "INVALID_EXTRAS_JSON") {
        notification.error({
          message: "Invalid JSON in Extra Settings",
          description:
            "Fix the JSON in the textarea or clear it, then try again.",
        });
        return;
      }
      throw e;
    }

    updateSettingsMutation.mutate(payload);
  };

  return (
    <Layout className={styles.layout}>
      <Row style={{ height: 32, justifyContent: "space-between" }}>
        <Col span={12}>
          <Typography.Title style={{ margin: 0 }} level={5}>
            Settings
          </Typography.Title>
        </Col>
      </Row>
      <Layout.Content
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Form
            form={form}
            requiredMark={false}
            onFinish={handleFinish}
            layout="vertical"
          >
            <Row gutter={[16, 16]}>
              {/* <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftsPerDay"
                  label="Drafts Per Day"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col> */}

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftsPerReferral"
                  label="Drafts Per Referral"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="forceUpdateApp"
                  label="Force Update App"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="usableDraftTokensPerDay"
                  label="Draft Tokens Limit Per Day"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="inAppPurchaseEnabled"
                  label="Enable In App Purchase"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="freeDraftTokensEnabled"
                  label="Enable Daily Draft Tokens"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftTokensPerDay"
                  label="Daily Draft Tokens"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    disabled={!freeDraftTokensEnabled}
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="viewSolutionLimitPerDay"
                  label="View Solution Limit Per Day"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="initialFreeSolutionLimit"
                  label="Initial Free Solution Views for New Users"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="start" gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="maintenanceModeEnabled"
                  label="Enable Maintenance Mode"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="offseasonAutoStartEnabled"
                  label="Enable Offseason Auto-Start"
                  tooltip="When enabled, full pre-season leagues auto-start the next season after the 24h offseason countdown."
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="start" gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="sessionReplayCount"
                  label="Session Replay Count"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="minScoreToUnlockRankedMode"
                  label="Min Score To Unlock Ranked Mode"
                  rules={[
                    {
                      type: "number",
                      min: 0,
                      message: "Must be a number greater than or equal to 0",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftsToUnlockRankedMode"
                  label="Drafts To Unlock Ranked Mode"
                  rules={[
                    {
                      type: "number",
                      min: 0,
                      message: "Must be a number greater than or equal to 0",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="maxFinishPositionToUnlockRankedMode"
                  label="Max Finish Position To Unlock Ranked Mode"
                  tooltip="Worst Unranked pool place (inclusive) that unlocks Ranked on Royale finish. Default 50."
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      message: "Must be a number greater than or equal to 1",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="rankedRoyaleDraftsPerDay"
                  label="Ranked Royale Drafts Per Day"
                  tooltip="Free qualification drafts per day (no tokens)"
                  rules={[
                    {
                      type: "number",
                      min: 1,
                      message: "Must be a number greater than or equal to 1",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="extras"
                  label="Extra Settings (JSON)"
                  tooltip="Arbitrary key-value pairs stored in Redis. Must be valid JSON."
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        try {
                          const parsed = JSON.parse(value as string) as unknown;
                          const isPlainObject =
                            typeof parsed === "object" &&
                            parsed !== null &&
                            !Array.isArray(parsed);
                          if (!isPlainObject) {
                            return Promise.reject(
                              new Error("Expected JSON object"),
                            );
                          }
                          return Promise.resolve();
                        } catch {
                          return Promise.reject(
                            new Error("Invalid JSON format"),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={10}
                    style={{ fontFamily: "monospace" }}
                    placeholder='{ "key": "value" }'
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end" style={{ marginTop: 16 }}>
              <Col>
                <Button
                  loading={updateSettingsMutation.isLoading}
                  disabled={updateSettingsMutation.isLoading}
                  size="middle"
                  type="primary"
                  htmlType="submit"
                >
                  Update
                </Button>
              </Col>
            </Row>
          </Form>
        </Space>
      </Layout.Content>
    </Layout>
  );
};

export default Settings;
